import { Separator } from '@heidi/design-system/separator';
import { type Editor, useEditorState } from '@tiptap/react';
import {
  PanelTopDashed,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
} from 'lucide-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  type CellAlign,
  getCurrentColumnAlign,
  isHeaderRowActive,
  setColumnAlign,
} from '../core';
import { disabledButtonClassName } from './disabled-button-class-name';
import { Toolbar } from './toolbar-namespace';

/**
 * Markdown-native-only table controls (header-row toggle, per-column GFM
 * alignment). The parent gates rendering on `tableCellSupportsAlign` —
 * vMarkdown/v1 schemas can't persist these.
 */
export const MarkdownTableControls = ({
  editor,
  unmappable = false,
}: {
  editor: Editor;
  /** The table cannot be mapped, so every control here would throw. */
  unmappable?: boolean;
}) => {
  const intl = useIntl();
  const { currentAlign, isHeaderRow } = useEditorState({
    editor,
    selector: ({ editor: instance }: { editor: Editor }) => ({
      currentAlign: getCurrentColumnAlign(instance),
      isHeaderRow: isHeaderRowActive(instance),
    }),
  });

  const unmappableTooltip = intl.formatMessage({
    defaultMessage: "This table's layout can't be edited",
    id: 'xDZkYcPvBw',
  });
  const unmappableProps = unmappable
    ? { 'aria-disabled': true, className: disabledButtonClassName }
    : {};

  const onToggleHeaderRow = useCallback(() => {
    if (unmappable) return;
    editor.chain().focus().toggleHeaderRow().run();
  }, [editor, unmappable]);

  const onSetAlign = useCallback(
    (align: CellAlign) => {
      if (unmappable) return;
      // Clicking the active alignment clears back to the GFM default (---).
      setColumnAlign(editor, currentAlign === align ? null : align);
    },
    [editor, currentAlign, unmappable]
  );

  return (
    <>
      <Separator orientation="vertical" className="mx-0.5 h-4" />
      <Toolbar.Button
        tooltip={
          unmappable
            ? unmappableTooltip
            : intl.formatMessage({
                defaultMessage: 'Toggle header row',
                id: 'yy68o/ecQK',
              })
        }
        onClick={onToggleHeaderRow}
        {...unmappableProps}
        active={isHeaderRow}
        type="button"
        // The tooltip renders in a portal on hover, so the icon-only button has
        // no accessible name for an e2e locator to reach.
        data-testid="table-menu-toggle-header-row"
      >
        <PanelTopDashed size={14} />
      </Toolbar.Button>
      <Separator orientation="vertical" className="mx-0.5 h-4" />
      <Toolbar.Button
        tooltip={
          unmappable
            ? unmappableTooltip
            : intl.formatMessage({
                defaultMessage: 'Align column left',
                id: 'M4JSV9zRLI',
              })
        }
        onClick={() => onSetAlign('left')}
        {...unmappableProps}
        active={currentAlign === 'left'}
        type="button"
      >
        <TextAlignStart size={14} />
      </Toolbar.Button>
      <Toolbar.Button
        tooltip={
          unmappable
            ? unmappableTooltip
            : intl.formatMessage({
                defaultMessage: 'Align column center',
                id: 'ajJ2+sqb1T',
              })
        }
        onClick={() => onSetAlign('center')}
        {...unmappableProps}
        active={currentAlign === 'center'}
        type="button"
      >
        <TextAlignCenter size={14} />
      </Toolbar.Button>
      <Toolbar.Button
        tooltip={
          unmappable
            ? unmappableTooltip
            : intl.formatMessage({
                defaultMessage: 'Align column right',
                id: 'mSubxdbRMP',
              })
        }
        onClick={() => onSetAlign('right')}
        {...unmappableProps}
        active={currentAlign === 'right'}
        type="button"
      >
        <TextAlignEnd size={14} />
      </Toolbar.Button>
    </>
  );
};
